import { Injectable, signal, WritableSignal } from '@angular/core'
import flagsmith from '@flagsmith/flagsmith'
import { environment } from '@environments/environment'

// This has to be extended with all feature flag IDs
export type FeatureFlag =
  'search'
  | 'deployed_hash'
  | 'manual_logout'

@Injectable({
  providedIn: 'root'
})
/**
 * Service for managing feature flags using Flagsmith.
 * Implemented according to https://www.flagsmith.com/blog/angular-feature-flag/
 * Requires the environment variables `flagsmithApiUrl` and `flagsmithEnvironmentId` to be set.
 */
export class FeatureFlagService {
  public readonly featureFlags: WritableSignal<Record<FeatureFlag, boolean>> = signal<Record<FeatureFlag, boolean>>({
    // Use a safe default before init. This will be updated on init/onChange.
    search: false,
    deployed_hash: false,
    manual_logout: false
  })

  public async init(): Promise<void> {
    try {
      await flagsmith.init({
        api: environment.flagsmithApiUrl,
        environmentID: environment.flagsmithEnvironmentId,
        onChange: (_, params) => {
          const changedFlags: Partial<Record<FeatureFlag, boolean>> = {}
          params.flagsChanged?.forEach((flag) => {
            if (flag === 'search') {
              changedFlags.search = flagsmith.hasFeature('search')
            }
          })

          this.featureFlags.update((currentFlags) => ({
            ...currentFlags,
            ...changedFlags
          }))
        },
        defaultFlags: {
          search: {
            enabled: false,
            value: ''
          },
          deployed_hash: {
            enabled: false,
            value: ''
          },
        }
      })

      this.featureFlags.set({
        search: flagsmith.hasFeature('search'),
        deployed_hash: flagsmith.hasFeature('deployed_hash'),
        manual_logout: flagsmith.hasFeature('manual_logout')
      })
    } catch (error) {
      console.error('Failed to initialize Flagsmith:', error)
    }
  }
}
